import SoftphoneLib from '../../../../softphone/softphone.es';
import config from 'config';

const {
  constants: {
    API: { PRICING_UPDATE_URL, BATCHDIALER_BASE_URL, BATCHDIALER_POSTBACK_URL },
  },
} = config;

const REGISTRATION_TIMEOUT_MS = 20000;
// The library unmounts a destroyed instance 1s later; this is a safety net if "destroyed" never fires.
const TEARDOWN_TIMEOUT_MS = 3000;
function withTimeout(promise, ms, message) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

class SoftphoneService {
  constructor() {
    this.softphone = null;
    this.isRegistered = false;
    this.registrationKey = null;
    this.registrationKeyPromise = null;
    this.callerId = null;
    this.micStream = null;
    this.onCallEnded = null;
    this.onCleanup = null;
    this._registeringPromise = null;
    this._micPermissionPromise = null;
    // Teardown of the previous instance still in progress. A new instance must not connect
    // before it finishes: the old instance's deferred unmount disconnects the shared socket.
    this.pendingTeardown = null;
  }

  isRegistered = this.softphone && this.isRegistered;

  async fetchRegistrationKey() {
    console.log('SoftphoneService fetching Temp API key');
    const res = await fetch(`${PRICING_UPDATE_URL}/ps/integrations/batch-dialer-api-key`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-auth-token': window.appAuthToken,
      },
    });

    if (!res.ok) {
      throw new Error('Failed to fetch batch dialer API key');
    }

    const data = await res.json();
    return data?.apiKey;
  }

  async getRegistrationKey() {
    if (this.registrationKeyPromise) {
      return this.registrationKeyPromise;
    }

    this.registrationKeyPromise = this.fetchRegistrationKey().finally(() => {
      this.registrationKeyPromise = null;
    });

    return this.registrationKeyPromise;
  }

  async prewarmRegistrationKey() {
    if (this.registrationKey != null) return this.registrationKey;
    try {
      this.registrationKey = await this.getRegistrationKey();
      console.log('[SoftphoneService] Registration key pre-warmed');
      return this.registrationKey;
    } catch (e) {
      console.error('[SoftphoneService] Failed to pre-warm registration key', e);
      return null;
    }
  }

  async ensureRegistered(agentInfo, callerId) {
    if (this.isRegistered) return;

    if (this._registeringPromise) {
      return this._registeringPromise;
    }

    this._registeringPromise = this.register(agentInfo, callerId).finally(() => {
      this._registeringPromise = null;
    });

    return this._registeringPromise;
  }

  async prewarmRegistration(agentInfo, callerId) {
    try {
      await this.ensureRegistered(agentInfo, callerId);
      console.log('[SoftphoneService] Prewarm registration complete');
    } catch (e) {
      console.error('[SoftphoneService] Prewarm registration failed, will retry on click', e);
    }
  }

  async prewarmMicPermission(microphonePermissionHandler) {
    if (this.micStream) return this.micStream;
    if (this._micPermissionPromise) return this._micPermissionPromise;

    this._micPermissionPromise = microphonePermissionHandler
      .getPermission()
      .then((response) => {
        if (!response.error) {
          this.micStream = response.stream;
        }
        return response;
      })
      .catch(() => {})
      .finally(() => {
        this._micPermissionPromise = null;
      });

    return this._micPermissionPromise;
  }

  registerOnCleanup(eventHandler) {
    this.onCleanup = eventHandler;
  }

  registerOnCallEnded(eventHandler) {
    this.onCallEnded = eventHandler;
  }

  /**
   * Destroys `instance` and resolves once the library has finished tearing it down ("destroyed").
   * Never destroys the same instance twice: a second destroy() schedules a second deferred
   * disconnect that would kill whichever instance is current by then.
   */
  teardown(instance, { alreadyDestroying = false } = {}) {
    if (!instance) return Promise.resolve();

    if (!instance.psTeardown) {
      instance.psTeardown = new Promise((resolve) => {
        instance.on('destroyed', resolve);
        setTimeout(resolve, TEARDOWN_TIMEOUT_MS);
      });
      if (!alreadyDestroying) instance.destroy();
    }

    return instance.psTeardown;
  }

  // Events are bound to one instance; events from an instance that is no longer current are
  // ignored, so a late "destroyed" from the previous call can't wipe out the new softphone.
  attachEvents(instance) {
    const isCurrent = () => this.softphone === instance;

    const stopMic = () => {
      if (this.micStream) {
        this.micStream.getTracks().forEach((track) => {
          if (track.kind === 'audio') {
            track.stop();
          }
        });
        this.micStream = null;
        console.log('[SoftphoneService] Microphone stopped');
      }
    };

    instance.on('register', () => {
      if (!isCurrent()) return;
      this.isRegistered = true;
      console.log('SoftphoneService after register state ' + this.isRegistered);
    });

    instance.on('hangup', () => {
      console.log('[SoftphoneService] hangup');
      stopMic();
      if (!isCurrent()) return;

      // The library already called destroy() itself on hangup; wait for it to finish
      // before re-registering instead of racing its deferred unmount.
      this.pendingTeardown = this.teardown(instance, { alreadyDestroying: true });
      this.cleanup();
      console.log('SoftphoneService after hangup state ' + this.isRegistered);

      this.onCallEnded?.();
      this.pendingTeardown.then(() => this.onCleanup?.());
    });

    instance.on('hide', () => {
      console.log('[SoftphoneService] hide');
      if (isCurrent()) {
        instance.hangup();
      }
    });

    instance.on('destroyed', () => {
      console.log('[SoftphoneService] destroyed');
      if (!isCurrent()) return;

      stopMic();
      this.cleanup();
      console.log('SoftphoneService after destroy state ' + this.isRegistered);
      this.onCleanup?.();
    });

    instance.on('idle', () => {
      console.log('[SoftphoneService] idle');
      if (!isCurrent()) return;

      stopMic();
      this.pendingTeardown = this.teardown(instance);
      this.cleanup();
      console.log('SoftphoneService after idle state ' + this.isRegistered);

      this.onCallEnded?.();
      this.pendingTeardown.then(() => this.onCleanup?.());
    });

    instance.on('error', (err) => {
      console.error('Softphone error', err);
    });
  }

  cleanup() {
    this.softphone = null;
    this.isRegistered = false;
    this.registrationKey = null;
    this.callerId = null;
    this.micStream = null;
  }

  async register(agentInfo, callerId) {
    if (this.softphone) {
      this.pendingTeardown = this.teardown(this.softphone);
      this.cleanup();
    }

    // A click right after a call ends waits here for the previous instance to finish
    // tearing down, so the new instance's socket isn't closed by the old unmount.
    if (this.pendingTeardown) {
      await this.pendingTeardown;
    }

    if (this.registrationKey == null) {
      this.registrationKey = await this.getRegistrationKey();
      console.log('SoftphoneService API key found');
    }
    this.callerId = callerId;

    if (!this.registrationKey) {
      throw new Error('Softphone registration key missing');
    }

    const instance = new SoftphoneLib.Softphone();
    instance.enableAutoTests = false;
    this.softphone = instance;

    this.attachEvents(instance);

    try {
      await withTimeout(
        new Promise((resolve, reject) => {
          instance.on('register', () => {
            if (this.softphone !== instance) return;
            this.isRegistered = true;
            resolve();
          });

          instance.on('error', reject);

          instance.register(this.registrationKey, {
            backendAPI: `${BATCHDIALER_BASE_URL}/api/`,
            agent: agentInfo,
            theme: 'propstream',
            minimized: false,
            addonCallIntelligence: true,
            addonCallIntelligenceOptions: {
              trialBadge: false,
              propertyLoad: false,
            },
          });
        }),
        REGISTRATION_TIMEOUT_MS,
        'Timed out registering with softphone service'
      );
    } catch (e) {
      // Destroy the instance THIS call created, even if the service reference has moved on,
      // so a failed registration never leaves a zombie widget that duplicates later calls.
      if (this.softphone === instance) this.cleanup();
      this.pendingTeardown = this.teardown(instance);
      throw e;
    }
  }

  async dial({
    contact,
    propertyInfo,
    userId,
    accountId: _accountId,
    marketingCampaignId,
    dialerCampaignId,
  }) {
    if (!this.softphone || !this.isRegistered) {
      throw new Error('Softphone is not registered');
    }

    const rawNumber = contact?.phonenumber;
    const normalizedNumber = rawNumber ? String(rawNumber).replace(/[^\d+]/g, '') : rawNumber;

    let postbackUrl =
      `${BATCHDIALER_POSTBACK_URL}/api/webhook/batchdialer/log/add` +
      `?contactId=${contact.contactId}` +
      `&phoneNumber=${normalizedNumber}` +
      `&userId=${userId}`;

    if (marketingCampaignId) {
      postbackUrl += `&marketingCampaignId=${marketingCampaignId}`;
    }
    if (dialerCampaignId) {
      postbackUrl += `&dialerCampaignId=${dialerCampaignId}`;
    }

    this.softphone.setPostbackURL(postbackUrl);

    return this.softphone.dial({
      contact: { ...contact, phonenumber: normalizedNumber },
      callerID: this.callerId,
      propertyInfo,
    });
  }

  destroy() {
    if (this.softphone) {
      this.pendingTeardown = this.teardown(this.softphone);
      this.cleanup();
    }
  }
}

export default new SoftphoneService();
